/**
 * DetachPolicyModal — confirmation modal for POST /account-insurance/{id}/detach
 * (#275, 2026-06-08).
 *
 * Detach takes an Active Policy → Inactive limbo (agreement_id NULL),
 * and cascades each Active Indirect Account owned by the Policy to
 * Inactive (asymmetric reversal — Inactive Accounts already in that
 * state are untouched). The asymmetry matters: re-attaching the Policy
 * later via #272 attach does NOT auto-reactivate the Indirect Accounts.
 *
 * Friction model matches CascadeStatusModal: clear preview of the
 * downstream impact + explicit Confirm gate. Charter §5
 * (human-in-the-loop) — detach is a state transition with cross-row
 * cascade and needs the operator's deliberate yes.
 */
import {
  Alert, Badge, Button, Group, Modal, Stack, Text, Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconUnlink } from '@tabler/icons-react';

import { ApiError } from '../api/client';
import type { AccountInsurance } from '../api/types';
import { useDetachPolicy } from '../hooks/useAccountInsurance';
import { useAlertModal } from './AlertModalProvider';

interface Props {
  opened: boolean;
  onClose: () => void;
  policy: AccountInsurance;
  /** Optional success handler — used by detail pages to navigate or
   *  refresh local UI after the detach lands. Ignored when onStage is
   *  provided (the parent's Save handler owns post-commit behaviour). */
  onSuccess?: () => void;
  /** Phase 1.5 (#375, 2026-06-09): when provided, the modal becomes a
   *  STAGING gesture instead of an immediate commit. The Confirm button
   *  reads "Stage Detach" and calls onStage(); the parent's Save handler
   *  later commits via useDetachPolicy. The footprint preview is shown
   *  identically — only the verb changes. When omitted, the modal keeps
   *  its original immediate-commit semantics for any other callsites. */
  onStage?: () => void;
}

export function DetachPolicyModal({ opened, onClose, policy, onSuccess, onStage }: Props) {
  const detachMut = useDetachPolicy();
  const { showAlert } = useAlertModal();

  const indirectCount = policy.account_ids.length;
  const isStaging = !!onStage;

  function handleDetach() {
    if (isStaging) {
      onStage!();
      onClose();
      return;
    }
    detachMut.mutate(policy.id, {
      // #26 (2026-06-10): detach is maker-checker now — surface the RM
      // pending case on this legacy immediate path too.
      onSuccess: (r) => {
        if (r.pending) {
          notifications.show({
            color: 'orange',
            message: 'Detach filed for approval — the policy stays attached until Compliance approves.',
          });
        }
        onClose();
        onSuccess?.();
      },
      onError: (err) => {
        if (err instanceof ApiError) {
          showAlert({
            color: 'red',
            title: 'Cannot detach Policy',
            message: err.detail,
          });
        }
      },
    });
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="lg"
      title={
        <Group gap="xs">
          <IconUnlink size={20} />
          <Title order={4}>Detach Policy from Agreement</Title>
        </Group>
      }
    >
      <Stack>
        <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
          Detach moves <strong>{policy.insurer} — {policy.policy_number}</strong>{' '}
          to <Badge color="yellow" variant="light">Inactive</Badge> limbo. The
          Policy will no longer belong to any Agreement until it is re-attached.
        </Alert>

        {indirectCount > 0 && (
          <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
            <Text size="sm">
              <strong>{indirectCount}</strong> Indirect Account{indirectCount === 1 ? '' : 's'}{' '}
              owned by this Policy will cascade to{' '}
              <Badge color="yellow" variant="light">Inactive</Badge>.
            </Text>
            <Text size="xs" c="dimmed" mt={4}>
              Asymmetric reversal: re-attaching this Policy later does NOT
              auto-reactivate these Accounts. Each must be reactivated
              individually if needed.
            </Text>
          </Alert>
        )}

        {indirectCount === 0 && (
          <Text size="sm" c="dimmed">
            No Indirect Accounts are owned by this Policy — only the Policy
            itself will change state.
          </Text>
        )}

        <Group justify="space-between" pt="sm">
          <Button variant="default" onClick={onClose}
            disabled={detachMut.isPending}>
            Cancel
          </Button>
          <Button color="yellow"
            leftSection={<IconUnlink size={14} />}
            onClick={handleDetach}
            loading={!isStaging && detachMut.isPending}>
            {isStaging ? 'Stage Detach' : 'Detach Policy'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
